/**
 * Telefoonnummers naar E.164 (ADR-0065).
 *
 * De invoervelden accepteren wat een Nederlander intikt -- `06 12 34 56 78`,
 * `+31 (0)6-12345678`, `0031612345678` -- maar `users.phone_e164` bewaart maar
 * een vorm: `+31612345678`. Deze module is de plek waar dat gebeurt aan de
 * clientkant; `App\Support\Contact\PhoneNumber` doet exact hetzelfde in de API,
 * want de server vertrouwt geen genormaliseerde invoer van een client.
 *
 * Bewust NL-only, gelijk aan `formRules.text_field_rules.isNlPhone`: een regel
 * die minder accepteert dan de normalisatie zou nummers doorlaten die het veld
 * afkeurt, en andersom.
 *
 * Geen imports: `scripts/test-phone-e164.mjs` draait dit bestand in een `vm`
 * zonder module-resolutie.
 */

/** Alles wat mensen tussen de cijfers zetten. */
const SEPARATORS = /[\s\-().]/g;

/**
 * `+31` / `0031` / `0` gevolgd door 9 cijfers (mobiel `6…`, vast `[1-9]…`).
 *
 * De `0?` achter de landcode vangt de Nederlandse schrijfwijze `+31 (0)6…`: de
 * haakjes zijn dan al weg en de nationale nul staat er nog. Zonder die nul
 * accepteren we `+31612345678` en `0612345678` maar niet de notatie die op elk
 * visitekaartje staat. Een echt E.164-nummer heeft nooit een 0 na 31, dus dit
 * maakt niets dubbelzinnig.
 */
const NL_NUMBER = /^(?:(?:\+31|0031)0?|0)(6\d{8}|[1-9]\d{8})$/;

/**
 * Of deze bedrijfspagina om een telefoonnummer vraagt (ADR-0091). Afwezig of
 * null is aan: zo verandert er voor een bestaande pagina niets tot een admin de
 * schakelaar omzet. `ActivityController::asksPhone()` leest het net zo.
 */
export function asksPhone(page) {
  const flag = page?.settings?.ask_phone;
  return flag === undefined || flag === null ? true : !!flag;
}

/**
 * Het genormaliseerde nummer, of `null` als er niets bruikbaars in staat.
 * Een lege waarde is geen fout -- of het veld verplicht is, beslist het scherm.
 */
export function toE164(value) {
  if (value === null || value === undefined) return null;

  const cleaned = String(value).trim().replace(SEPARATORS, '');
  if (!cleaned) return null;

  const match = NL_NUMBER.exec(cleaned);
  return match ? `+31${match[1]}` : null;
}

/** Of dit door de normalisatie komt. Een lege waarde telt als geldig. */
export function isNormalisablePhone(value) {
  if (value === null || value === undefined || String(value).trim() === '') return true;
  return toE164(value) !== null;
}

export default { asksPhone, toE164, isNormalisablePhone };
